import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { FormHelpers, OnFormInitialValuesChange } from '../../../../../screens/utils';
import {
  getNaceCodesOptionsListBySection,
  naceCodesOptionsList,
  naceSectionsOptionsList,
} from '../../../../../services/common/naceCodes';
import { journeyDataActions, journeyDataSelectors } from '../../../../../services/redux/features/journeyData.slice';
import { FormSectionEditButton } from '../../../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../../../FormSectionSummaryTitle';
import { createFormInputs } from '../../../components/formInputs';
import { CompanyDetailsFormModel_OB_OL } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';
import { formAutocomplete } from '../../../../../config';
import { calculateYearsSinceDate } from '../../../../../services/common/miscUtils';
import { BusinessCustomerTypesEnum } from '../../../../../screens/NewQuotation/CustomerDataPage/customerDataPage.model';

type Props = {
  formHelpers: FormHelpers;
};

const croOptionalFor = [BusinessCustomerTypesEnum.partnership, BusinessCustomerTypesEnum.other];

/**
 * - OtherBusiness (LTD, PLC, Other, Unlimited, Partnership)
 * - OperatingLease
 */
export const CompanyDetailsForm_OB_OL: React.FC<Props> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const businessType = useSelector(journeyDataSelectors.businessCustomerType);
  const [validationSchema] = useState<ObjectSchema<Partial<CompanyDetailsFormModel_OB_OL>>>(() =>
    getValidationSchema(!croOptionalFor.includes(businessType!))
  );

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (/* values: ThisFormModel */) => {
    /* start loader */
    Promise.resolve(/* submit */).then(onFormSubmitSuccess).catch(/* on error */).finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`companyDetailsForm_OL:title:business`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent onFormInitialValuesChange={onFormInitialValuesChange} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  formHelpers: FormHelpers;
  onFormInitialValuesChange: OnFormInitialValuesChange;
};

export const FormContent = ({ formHelpers }: FormContentProps) => {
  const [inputs] = useState(() => createFormInputs('companyDetailsForm_OL'));
  const dispatch = useDispatch();
  const { values, submitForm, setFieldValue } = useFormikContext<CompanyDetailsFormModel_OB_OL>();
  const { isDisabledFormSummary } = formHelpers;

  useEffect(() => {
    dispatch(journeyDataActions.updateData(requestMapper(values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const [businessSubTypeOptions, setBusinessSubTypeOptions] = useState(() =>
    values.businessType ? getNaceCodesOptionsListBySection(values.businessType) : naceCodesOptionsList
  );

  // Reset business sub type if business type changed, and sub type is no longer valid.
  useEffect(() => {
    const newBusinessSubTypeOptions = values.businessType
      ? getNaceCodesOptionsListBySection(values.businessType)
      : naceCodesOptionsList;

    setBusinessSubTypeOptions(newBusinessSubTypeOptions);

    if (newBusinessSubTypeOptions.some(({ value }) => value === values.businessSubType)) return;
    setFieldValue('businessSubType', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.businessType]);

  const disableForm = isDisabledFormSummary;
  const yearsSinceIncorporation = calculateYearsSinceDate(values.dateOfIncorporation);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.croNumber()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.croNumberButton(!values.croNumber)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.tradingName()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.legalName()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.vatLiable()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.vatNumber()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="5/12" s="1/1" m="1/3" >{inputs.dateOfIncorporation()}</Layout.Item>
            <Layout.Item default="1/12" s="1/1" m="1/6" className="u-mt u-pt-xsmall u-pl-none u-text-left@s u-mt-none@s u-pt-none@s u-ml@s">{yearsSinceIncorporation !== null && `${yearsSinceIncorporation} ${yearsSinceIncorporation === 1 ? 'Year' : 'Years'}`}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.businessType(naceSectionsOptionsList)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>           
            <Layout.Item default="1/2" s="1/1">{inputs.businessSubType(businessSubTypeOptions)}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.countryCode()}</Layout.Item>
            <Layout.Item default="1/4" s="1/1">{inputs.phoneNumber()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>    
            <Layout.Item default="1/2" s="1/1">{inputs.email()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.fleetSize()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>       
            <Layout.Item default="1/2" s="1/1">{inputs.companyClassification()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.turnover()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>

      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers}/>
    </Form>
  );
};
